import { useState, useEffect } from 'react'
import { Download, X, Smartphone, Monitor, Tablet, CheckCircle2, Info } from 'lucide-react'

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

const InstallPWA = ({ onHome = false }: { onHome?: boolean }) => {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null)
  const [isInstalled, setIsInstalled] = useState(false)
  const [showInstructions, setShowInstructions] = useState(false)
  const [deviceType, setDeviceType] = useState<'ios' | 'android' | 'desktop' | 'unknown'>('unknown')

  useEffect(() => {
    // Detectar si ya está instalado
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches ||
      (window.navigator as any).standalone ||
      document.referrer.includes('android-app://')

    if (isStandalone) {
      setIsInstalled(true)
      return
    }

    // Detectar tipo de dispositivo
    const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera
    const isIOS = /iPad|iPhone|iPod/.test(userAgent) && !(window as any).MSStream
    const isAndroid = /android/i.test(userAgent)
    const isDesktop = !isIOS && !isAndroid

    if (isIOS) {
      setDeviceType('ios')
    } else if (isAndroid) {
      setDeviceType('android')
    } else if (isDesktop) {
      setDeviceType('desktop')
    }

    // Escuchar el evento beforeinstallprompt (Chrome, Edge, etc.)
    const handleBeforeInstallPrompt = (e: Event) => {
      // Prevenir el banner automático solo si vamos a mostrar nuestro botón
      // Si no hay botón visible, permitir el banner nativo
      e.preventDefault()
      setDeferredPrompt(e as BeforeInstallPromptEvent)
    }

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt)

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt)
    }
  }, [])

  const handleInstallClick = async () => {
    if (deferredPrompt) {
      // Mostrar el prompt nativo
      await deferredPrompt.prompt()
      const { outcome } = await deferredPrompt.userChoice
      
      if (outcome === 'accepted') {
        console.log('Usuario aceptó instalar la PWA')
      } else {
        console.log('Usuario rechazó instalar la PWA')
      }
      
      setDeferredPrompt(null)
    } else {
      // Mostrar instrucciones manuales
      setShowInstructions(true)
    }
  }

  // No mostrar si ya está instalado
  if (isInstalled) {
    return null
  }

  // Siempre mostrar el botón, incluso si no hay prompt nativo
  // Esto permite mostrar instrucciones manuales en iOS y otros navegadores

  const getInstructions = () => {
    switch (deviceType) {
      case 'ios':
        return {
          title: 'Instalar en iPhone/iPad',
          steps: [
            'Abre el menú de compartir (botón de compartir)',
            'Desplázate hacia abajo y toca "Añadir a pantalla de inicio"',
            'Toca "Añadir" en la esquina superior derecha',
            '¡Listo! La app aparecerá en tu pantalla de inicio'
          ],
          icon: Smartphone
        }
      case 'android':
        return {
          title: 'Instalar en Android',
          steps: [
            'Toca el menú (tres puntos) en la esquina superior derecha',
            'Selecciona "Añadir a pantalla de inicio" o "Instalar app"',
            'Confirma la instalación',
            '¡Listo! La app aparecerá en tu pantalla de inicio'
          ],
          icon: Smartphone
        }
      case 'desktop':
        return {
          title: 'Instalar en Escritorio',
          steps: [
            'Busca el icono de instalación en la barra de direcciones del navegador',
            'O ve al menú del navegador y busca "Instalar ColmaFácil"',
            'Haz clic en "Instalar"',
            '¡Listo! La app se abrirá como una aplicación independiente'
          ],
          icon: Monitor
        }
      default:
        return {
          title: 'Instalar ColmaFácil',
          steps: [
            'Busca la opción de instalación en el menú de tu navegador',
            'O busca "Añadir a pantalla de inicio" en las opciones',
            'Sigue las instrucciones de tu navegador',
            '¡Listo! Disfruta de ColmaFácil como app'
          ],
          icon: Tablet
        }
    }
  }

  const instructions = getInstructions()
  const IconComponent = instructions.icon

  if (showInstructions) {
    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-primary-600 to-primary-700 px-6 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                <IconComponent className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-xl font-bold text-white">Instalar ColmaFácil</h3>
            </div>
            <button
              onClick={() => setShowInstructions(false)}
              className="p-2 hover:bg-white/20 rounded-xl transition-colors"
            >
              <X className="w-5 h-5 text-white" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6">
            <div className="mb-6">
              <h4 className="text-lg font-bold text-gray-900 mb-4">{instructions.title}</h4>
              <ol className="space-y-3">
                {instructions.steps.map((step, index) => (
                  <li key={index} className="flex gap-3">
                    <div className="flex-shrink-0 w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center text-primary-700 font-bold">
                      {index + 1}
                    </div>
                    <p className="flex-1 text-gray-700 pt-1">{step}</p>
                  </li>
                ))}
              </ol>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex gap-3">
              <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-blue-900">
                Una vez instalada, la app funcionará sin conexión y se actualizará automáticamente.
              </p>
            </div>
          </div>

          {/* Footer */}
          <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end">
            <button
              onClick={() => setShowInstructions(false)}
              className="px-6 py-2.5 bg-primary-600 text-white rounded-xl hover:bg-primary-700 transition-colors font-medium"
            >
              Entendido
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className={`${onHome ? 'mb-6' : 'mb-4'}`}>
      <div className="bg-gradient-to-br from-primary-50 to-primary-100/50 border-2 border-primary-200 rounded-2xl p-4 sm:p-5 shadow-lg">
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0 w-12 h-12 sm:w-14 sm:h-14 bg-gradient-to-br from-primary-500 to-primary-600 rounded-2xl flex items-center justify-center shadow-lg">
            <Download className="w-6 h-6 sm:w-7 sm:h-7 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-base sm:text-lg font-bold text-gray-900 mb-1">
              Instala ColmaFácil
            </h3>
            <p className="text-xs sm:text-sm text-gray-600 mb-4">
              Instala la app para acceso rápido, notificaciones y mejor experiencia.
            </p>
            <div className="flex flex-col sm:flex-row gap-2">
              {deferredPrompt ? (
                <button
                  onClick={handleInstallClick}
                  className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-gradient-to-r from-primary-600 to-primary-700 text-white rounded-xl hover:from-primary-700 hover:to-primary-800 transition-all duration-200 active:scale-95 font-medium shadow-md hover:shadow-lg text-sm sm:text-base"
                >
                  <Download className="w-4 h-4 sm:w-5 sm:h-5" />
                  Instalar Ahora
                </button>
              ) : (
                <button
                  onClick={() => setShowInstructions(true)}
                  className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-gradient-to-r from-primary-600 to-primary-700 text-white rounded-xl hover:from-primary-700 hover:to-primary-800 transition-all duration-200 active:scale-95 font-medium shadow-md hover:shadow-lg text-sm sm:text-base"
                >
                  <Download className="w-4 h-4 sm:w-5 sm:h-5" />
                  Ver Instrucciones de Instalación
                </button>
              )}
              {deferredPrompt && (
                <button
                  onClick={() => setShowInstructions(true)}
                  className="px-4 py-2.5 bg-white border-2 border-primary-300 text-primary-700 rounded-xl hover:bg-primary-50 transition-colors font-medium text-sm sm:text-base"
                >
                  Ver Instrucciones
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default InstallPWA

